/* Mirrors andavo-dev's Nomad Badge (libs/client/web/shared/ui/nomad/src/lib/Badge).
   Reproduced 1:1 against the vendored tokens. Two additions beyond the upstream
   variant set, both built only from tokens that already exist in the vendored
   theme so they stay native:
     - `feature` is the same as upstream `feature-accent` (kept the canonical
       name too); the shorter alias is what the shell/expense screens reference.
     - `blue` uses the accent-blue token family (present in styles.css) for an
       informational pill — upstream has no `blue` but the expense Reports screen
       wants a neutral-informational status color distinct from action/success. */
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../util/cn';

const badgeVariants = cva(
  'inline-flex items-center justify-center font-inter font-semibold tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-border-action focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'text-content-primary aria-disabled:text-content-primary-disabled',
        action:
          'text-content-action aria-disabled:text-content-action-disabled',
        danger:
          'text-content-danger aria-disabled:text-content-danger-disabled',
        success:
          'text-content-success aria-disabled:text-content-success-disabled',
        warning:
          'text-content-warning aria-disabled:text-content-warning-disabled',
        'feature-accent': 'text-content-feature-accent',
        feature: 'text-content-feature-accent',
        blue: 'text-[hsl(var(--accent-blue-700))]',
      },
      borderRadius: {
        square: 'rounded',
        round: 'rounded-full',
      },
      size: {
        default: 'px-2 py-1 text-xs [&>svg]:text-base',
        large: 'justify-center px-4 py-2 text-sm [&>svg]:text-xl',
        small: 'h-5 px-1 py-1 text-xs [&>svg]:text-xs',
      },
      intent: {
        default: '',
        background: '',
        outline: 'border border-border-subtle',
      },
    },
    compoundVariants: [
      {
        variant: 'action',
        intent: 'background',
        class:
          'bg-surface-action-subtle aria-disabled:bg-surface-action-subtle',
      },
      {
        variant: 'danger',
        intent: 'background',
        class:
          'bg-surface-danger-subtle aria-disabled:bg-surface-danger-subtle',
      },
      {
        variant: 'default',
        intent: 'background',
        class: 'bg-button-secondary aria-disabled:bg-button-secondary',
      },
      {
        variant: 'success',
        intent: 'background',
        class:
          'bg-surface-success-subtle aria-disabled:bg-surface-success-subtle',
      },
      {
        variant: 'warning',
        intent: 'background',
        class:
          'bg-surface-warning-subtle aria-disabled:bg-surface-warning-subtle',
      },
      {
        variant: 'feature-accent',
        intent: 'background',
        class: 'bg-surface-feature-accent-subtle',
      },
      {
        variant: 'feature',
        intent: 'background',
        class: 'bg-surface-feature-accent-subtle',
      },
      {
        variant: 'blue',
        intent: 'background',
        class: 'bg-[hsl(var(--accent-blue-50))]',
      },
    ],
    defaultVariants: {
      variant: 'action',
      size: 'default',
      intent: 'default',
      borderRadius: 'square',
    },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants>;

function Badge({
  className,
  variant,
  intent,
  size,
  borderRadius,
  ...props
}: BadgeProps) {
  return (
    <div
      className={cn(
        'aria-disabled:pointer-events-none aria-disabled:select-none',
        badgeVariants({ variant, size, borderRadius, intent }),
        className,
      )}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
